export type ApiClientConfig = {
  /** Fully qualified, including the version segment: `https://api.example.com/v1`. */
  baseUrl: string;
  /**
   * `include` for browsers, so the HttpOnly session cookies travel with each
   * request. Native clients send a bearer token through `getHeaders` instead and
   * pass `omit`.
   */
  credentials: RequestCredentials;
  /** Extra headers resolved per request - a bearer token, for instance. */
  getHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
  /**
   * Restores an expired session. The request layer calls this once for a group
   * of concurrent 401 responses, then replays each original request.
   */
  renewSession?: () => Promise<void>;
  /**
   * Called when the API rejects a request as unauthenticated. The web client
   * uses it only after renewal failed to clear local session state.
   */
  onUnauthorized?: () => void;
};

let config: ApiClientConfig | null = null;

export const configureApiClient = (next: ApiClientConfig): void => {
  config = next;
};

export const getApiClientConfig = (): ApiClientConfig => {
  if (!config) {
    throw new Error(
      'API client is not configured. Call configureApiClient() before any request.',
    );
  }

  return config;
};

/**
 * Which organization to act in. Held in memory rather than storage: it is a view
 * preference, and persisting it lets a stale value outlive the membership that
 * made it valid. The server decides the default when this is unset.
 */
let currentOrganizationId: string | null = null;

export const getCurrentOrganizationId = (): string | null =>
  currentOrganizationId;

export const setCurrentOrganizationId = (
  organizationId: string | null,
): void => {
  currentOrganizationId = organizationId;
};
